import { computed, getCurrentInstance, onBeforeUnmount, ref, watch } from 'vue';
import enUS from 'vue-datepicker-next/locale/en.es';
import huHU from 'vue-datepicker-next/locale/hu.es';

export type DatetimeType =
  | 'date'
  | 'datetime'
  | 'year'
  | 'month'
  | 'time'
  | 'week';

type Params = {
  type: DatetimeType;
  format?: string;
  valueType?: string;
  locale?: 'en' | 'hu' | string | null | undefined;
  onEscape?: () => void;
};

const VALUE_TYPE_MAP: Record<DatetimeType, string> = {
  date: 'YYYY-MM-DD',
  datetime: 'YYYY-MM-DD HH:mm',
  year: 'YYYY',
  month: 'YYYY-MM',
  time: 'HH:mm',
  week: 'gggg-[W]ww',
};

export function useItpDatetime({
  type,
  format,
  valueType,
  locale,
  onEscape,
}: Params) {
  const instance = getCurrentInstance();
  const $app = (instance?.proxy as any)?.$app ?? null;

  const effectiveType = type || 'date';

  const currentLocale = computed(() => locale || $app?.i18n?.locale || 'en');

  const lang = computed(() => (currentLocale.value === 'hu' ? huHU : enUS));

  const displayFormat = computed(() => {
    if (format) {
      return format;
    }

    if (effectiveType === 'date') {
      if ($app?.i18n?.dateFormat) {
        return $app.i18n.dateFormat as string;
      }
      if (currentLocale.value === 'hu') {
        return 'YYYY.MM.DD';
      }
    }

    if (effectiveType === 'month') {
      return 'YYYY-MM';
    }
    if (effectiveType === 'datetime') {
      return 'YYYY-MM-DD HH:mm';
    }

    return VALUE_TYPE_MAP[effectiveType] || 'YYYY-MM-DD';
  });

  const modelValueType = computed(() => {
    if (valueType) {
      return valueType;
    }
    return VALUE_TYPE_MAP[effectiveType];
  });

  const popupOpen = ref(false);

  function closeOnEscape(event: KeyboardEvent) {
    if (event.key !== 'Escape' || event.defaultPrevented) {
      return;
    }
    event.preventDefault();
    popupOpen.value = false;
    onEscape?.();
  }

  watch(popupOpen, (open) => {
    if (open) {
      document.addEventListener('keydown', closeOnEscape, true);
    } else {
      document.removeEventListener('keydown', closeOnEscape, true);
    }
  });

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', closeOnEscape, true);
  });

  return {
    lang,
    displayFormat,
    modelValueType,
    popupOpen,
  };
}
